import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Shield, Lock, Clock, Eye, ArrowRight, CheckCircle2, Loader2, ShieldAlert, ExternalLink, KeyRound, FileText, Link2, Zap } from 'lucide-react';
import { getPublicBrand } from '../lib/api';
import { useBrand } from '../context/BrandContext';

export default function BrandLanding({ embeddedBrand }) {
  const { slug } = useParams();
  const ctx = useBrand();
  const [brand, setBrand] = useState(embeddedBrand || ctx.brand);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (embeddedBrand) { setBrand(embeddedBrand); return; }
    if (ctx.brand && !slug) { setBrand(ctx.brand); return; }
    if (slug) {
      getPublicBrand(slug).then(setBrand).catch(() => setNotFound(true));
    }
  }, [slug, embeddedBrand, ctx.brand]);

  if (notFound) return (
    <div className="tv-gradient-soft min-h-[70vh] flex items-center justify-center py-16">
      <Card className="max-w-md w-full mx-4 p-8 text-center border border-slate-200">
        <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-red-50 text-red-600 border border-red-100"><ShieldAlert className="h-7 w-7" /></div>
        <h1 className="mt-4 text-2xl font-extrabold text-slate-900">Brand non trovato</h1>
        <p className="mt-2 text-slate-600 text-sm">Il brand richiesto non esiste o è stato disattivato.</p>
        <Button asChild className="mt-6 bg-[#0d63b0] hover:bg-[#0a4f8f]"><Link to="/">Torna alla home <ArrowRight className="h-4 w-4 ml-1.5" /></Link></Button>
      </Card>
    </div>
  );
  if (!brand) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#0d63b0]" /></div>;

  const primary = brand.primary_color || '#0d63b0';
  const accent = brand.accent_color || '#1e88d6';
  const hero_title = brand.hero_title || `${brand.name} · Condividi password in modo sicuro`;
  const hero_subtitle = brand.hero_subtitle || `Genera link cifrati che si auto-distruggono. Un servizio Secure Push offerto da ${brand.name}.`;
  // Build base path: custom domain uses "" (root), path-based uses "/b/:slug"
  const basePath = ctx.mode === 'branded-host' ? '' : `/b/${brand.slug}`;
  const newPushHref = `${basePath}/p/new`;

  return (
    <div>
      <section className="relative overflow-hidden" style={{ background: `linear-gradient(180deg, ${accent}12, #ffffff 60%)` }}>
        <div className="absolute inset-0 tv-dots opacity-40 pointer-events-none" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-16 pb-20 md:pt-20 md:pb-24">
          {/* Brand identity strip */}
          <div className="flex items-center gap-3">
            {brand.logo_url ? (
              <img src={brand.logo_url} alt={brand.name} className="h-10 w-auto" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
            ) : (
              <div className="h-10 w-10 rounded-lg flex items-center justify-center text-white font-bold" style={{ backgroundColor: primary }}>{brand.name[0]?.toUpperCase()}</div>
            )}
            <div>
              <div className="font-bold text-slate-900">{brand.name}</div>
              <div className="text-xs font-semibold uppercase tracking-widest" style={{ color: primary }}>Secure Push</div>
            </div>
            <div className="flex-1" />
            {brand.display_domain && <span className="hidden md:inline-flex text-xs text-slate-500 font-mono border border-slate-200 rounded-full px-3 py-1">{brand.display_domain}</span>}
          </div>

          <div className="mt-12 grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">{hero_title}</h1>
              <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl">{hero_subtitle}</p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Button asChild size="lg" className="h-12 px-6 text-white shadow-md" style={{ backgroundColor: primary }}>
                  <Link to={newPushHref}>Crea un push <ArrowRight className="h-4 w-4 ml-1.5" /></Link>
                </Button>
                {brand.company_url && (
                  <Button asChild size="lg" variant="outline" className="h-12 px-6">
                    <a href={brand.company_url} target="_blank" rel="noreferrer">Visita {brand.name} <ExternalLink className="h-4 w-4 ml-1.5" /></a>
                  </Button>
                )}
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-600">
                <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> Nessun account richiesto</span>
                <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> Cifratura AES-256</span>
                <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> Auto-eliminazione</span>
              </div>
            </div>

            <Card className="border border-slate-200 shadow-xl bg-white p-6 rounded-2xl">
              <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
                {brand.logo_url ? (
                  <img src={brand.logo_url} alt="" className="h-9 w-auto" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                ) : (
                  <div className="h-9 w-9 rounded-lg flex items-center justify-center text-white font-bold" style={{ backgroundColor: primary }}>{brand.name[0]?.toUpperCase()}</div>
                )}
                <div>
                  <div className="font-bold text-slate-900">{brand.name}</div>
                  <div className="text-xs text-slate-500">{brand.display_domain || 'secure.push'}</div>
                </div>
              </div>
              <div className="mt-5">
                <div className="text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5" style={{ color: primary }}><Lock className="h-3.5 w-3.5" /> Push sicuro · Riservato</div>
                <div className="mt-3 font-mono text-lg tracking-widest text-slate-400">••••••••••••</div>
                <div className="mt-4 flex flex-wrap gap-2 text-xs">
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200 px-2.5 py-1"><Clock className="h-3 w-3" /> Scade in 2gg</span>
                  <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 text-sky-700 border border-sky-200 px-2.5 py-1"><Eye className="h-3 w-3" /> 3 vis. rimaste</span>
                </div>
              </div>
              <Button asChild className="mt-6 w-full text-white" style={{ backgroundColor: primary }}>
                <Link to={newPushHref}>Crea il tuo push</Link>
              </Button>
            </Card>
          </div>
        </div>
      </section>

      <section id="features" className="py-16">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 grid md:grid-cols-3 gap-6">
          {[
            { i: Lock, t: 'Cifratura end-to-end', d: 'Contenuti cifrati AES-256. Solo il destinatario può rivelarli.' },
            { i: Clock, t: 'Scadenza automatica', d: 'Fino a 90 giorni o 100 visualizzazioni. Poi eliminazione definitiva.' },
            { i: Shield, t: `Fornito da ${brand.name}`, d: 'Servizio brandizzato, professionale e sempre disponibile.' },
          ].map((f) => (
            <Card key={f.t} className="tv-card-hover border border-slate-200 p-6">
              <div className="h-11 w-11 rounded-lg flex items-center justify-center" style={{ backgroundColor: primary + '18' }}>
                <f.i className="h-5 w-5" style={{ color: primary }} />
              </div>
              <h3 className="mt-4 text-lg font-bold text-slate-900">{f.t}</h3>
              <p className="mt-2 text-slate-600 text-sm leading-relaxed">{f.d}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS — informational section (no reviews, no reseller CTA) */}
      <section id="how-it-works" className="py-16 bg-slate-50/70 border-y border-slate-100">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <div className="text-xs font-semibold uppercase tracking-widest" style={{ color: primary }}>Come funziona</div>
            <h2 className="mt-3 text-3xl md:text-4xl font-extrabold text-slate-900">Tre passi. Semplice davvero.</h2>
            <p className="mt-4 text-slate-600">Niente software da installare. Funziona in qualsiasi browser. Sei operativo in un minuto.</p>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {[
              { n: 1, t: 'Incolla il tuo segreto', d: 'Password, chiave API, credenziali o testo riservato. Allega file se ti serve.', i: KeyRound },
              { n: 2, t: 'Imposta le regole', d: 'Visualizzazioni, tempo limite o entrambi. Aggiungi una passphrase per maggior sicurezza.', i: Clock },
              { n: 3, t: 'Condividi il link', d: 'Invialo come vuoi. Una volta visto o scaduto, il contenuto viene eliminato definitivamente.', i: Link2 },
            ].map((s) => (
              <Card key={s.n} className="tv-card-hover border border-slate-200 bg-white p-8 relative">
                <div className="absolute -top-4 left-8 h-8 w-8 rounded-full text-white text-sm font-bold flex items-center justify-center shadow" style={{ backgroundColor: primary }}>{s.n}</div>
                <s.i className="h-8 w-8 mt-2" style={{ color: primary }} />
                <h3 className="mt-4 text-xl font-bold text-slate-900">{s.t}</h3>
                <p className="mt-2 text-slate-600 text-sm leading-relaxed">{s.d}</p>
              </Card>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Button asChild size="lg" className="text-white h-12 px-6" style={{ backgroundColor: primary }}>
              <Link to={newPushHref}>Crea il tuo primo push <ArrowRight className="h-4 w-4 ml-1.5" /></Link>
            </Button>
          </div>
        </div>
      </section>

      {/* WHY / SECURITY DETAILS — no reviews, no reseller */}
      <section id="security" className="py-16">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <div className="text-xs font-semibold uppercase tracking-widest" style={{ color: primary }}>Perché sceglierlo</div>
            <h2 className="mt-3 text-3xl md:text-4xl font-extrabold text-slate-900">Più di un link. Una piattaforma di sicurezza.</h2>
          </div>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { i: Lock, t: 'Cifratura AES-256', d: 'Contenuti cifrati lato server con chiavi effimere. Neanche il database vede il testo in chiaro.' },
              { i: Clock, t: 'Scadenza flessibile', d: 'Fino a 90 giorni o 100 visualizzazioni. Il contenuto si auto-distrugge — non viene archiviato.' },
              { i: KeyRound, t: 'Passphrase opzionale', d: 'Aggiungi un livello di protezione con una passphrase condivisa fuori-canale.' },
              { i: FileText, t: 'File, testo, URL', d: 'Supporta note, credenziali, file allegati e URL da rivelare una sola volta.' },
              { i: Eye, t: 'Registro attività', d: 'Rivedi quando il push è stato visualizzato e da dove. Trasparenza completa.' },
              { i: Shield, t: 'GDPR & dati in EU', d: 'Nessun tracciamento di terze parti. Dati processati in Europa.' },
            ].map((f) => (
              <Card key={f.t} className="tv-card-hover border border-slate-200 p-6">
                <div className="h-11 w-11 rounded-lg flex items-center justify-center" style={{ backgroundColor: primary + '18' }}>
                  <f.i className="h-5 w-5" style={{ color: primary }} />
                </div>
                <h3 className="mt-4 text-lg font-bold text-slate-900">{f.t}</h3>
                <p className="mt-2 text-slate-600 text-sm leading-relaxed">{f.d}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA — brand-only, NO reseller/register mention */}
      <section id="cta" className="py-16">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <Card className="relative overflow-hidden border-0 shadow-xl">
            <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${primary}, ${accent})` }} />
            <div className="absolute inset-0 tv-dots opacity-20" />
            <div className="relative p-10 md:p-14 text-white text-center">
              <h2 className="text-3xl md:text-4xl font-extrabold">Pronto a condividere i segreti nel modo giusto?</h2>
              <p className="mt-4 text-white/90 max-w-2xl mx-auto">Nessun account, nessuna installazione, nessun tracciamento. Un link cifrato che scompare quando non serve più.</p>
              <div className="mt-8 flex flex-wrap justify-center gap-3">
                <Button asChild size="lg" className="bg-white hover:bg-slate-50 h-12 px-6 font-semibold shadow" style={{ color: primary }}>
                  <Link to={newPushHref}><Zap className="h-4 w-4 mr-1.5" /> Crea Push ora</Link>
                </Button>
                {brand.company_url && (
                  <Button asChild size="lg" variant="outline" className="h-12 px-6 border-white/40 text-white hover:bg-white/10 hover:text-white">
                    <a href={brand.company_url} target="_blank" rel="noreferrer">Visita {brand.name} <ExternalLink className="h-4 w-4 ml-1.5" /></a>
                  </Button>
                )}
              </div>
              <div className="mt-6 text-xs text-white/80">AES-256 cifrato · Dati in EU · GDPR · Auto-eliminazione</div>
            </div>
          </Card>
        </div>
      </section>

      {/* Custom-branded landing - no Tecnologie references, no reviews, no reseller CTA */}
    </div>
  );
}
